import { useEffect, useRef } from 'react';

/** Logical artwork size (matches previous 240×280) scaled +26.5% for display (1.15×1.1). */
const SCALE = 1.265;
const W = Math.round(240 * SCALE);
const H = Math.round(280 * SCALE);

/** `Tng.value` from DB may be `2.00` or `RM2.00` — always show as `RM …` in the white box. */
function formatTngValueForCanvas(raw: string | null | undefined): string {
  const v = (raw ?? '').trim();
  if (!v) return 'RM 2.00';
  const withoutPrefix = v.replace(/^RM\s*/i, '').trim();
  return withoutPrefix ? `RM ${withoutPrefix}` : 'RM 2.00';
}

type Props = {
  pin: string;
  /** Raw `Tng.value` (e.g. `2.00`); displayed as `RM …` in the white area. */
  valueLabel?: string | null;
  expiryLabel?: string | null;
};

/**
 * Draws `/images/TnG.jpg` and overlays PIN / value / expiry like `ploom-cd/tng_canvas.js`
 * (value in white box with RM prefix, PIN centered, expiry to the right of the printed label).
 */
export default function TngRewardCanvas({ pin, valueLabel, expiryLabel }: Props) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = W;
    canvas.height = H;

    const img = new Image();
    img.decoding = 'async';
    img.src = '/images/TnG.jpg';

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      ctx.drawImage(img, 0, 0, W, H);
      ctx.fillStyle = '#000';

      const rm = formatTngValueForCanvas(valueLabel);
      ctx.textAlign = 'center';
      ctx.font = `bold ${Math.round(16 * SCALE)}px Arial`;
      ctx.fillText(rm, W / 2, Math.round(200 * SCALE));

      ctx.font = `bold ${Math.round(20 * SCALE)}px Arial`;
      ctx.fillText(pin, W / 2, Math.round(233 * SCALE));

      if (expiryLabel?.trim()) {
        ctx.font = `bold ${Math.round(16 * SCALE)}px Arial`;
        ctx.textAlign = 'left';
        /** Just right of “PIN Expiry:” on the artwork (x tuned for alignment). */
        ctx.fillText(expiryLabel.trim(), Math.round(92 * SCALE), Math.round(262 * SCALE));
      }
    };

    img.onload = draw;
    if (img.complete) {
      draw();
    }
    img.onerror = () => {
      ctx.fillStyle = '#fff';
      ctx.font = '14px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Could not load voucher image.', W / 2, Math.round(H / 2));
    };
  }, [pin, valueLabel, expiryLabel]);

  return (
    <canvas
      ref={ref}
      width={W}
      height={H}
      className="mx-auto block h-auto w-full overflow-hidden rounded-2xl"
      style={{ maxWidth: W }}
      aria-label="Touch n Go eWallet reload PIN voucher"
    />
  );
}
